<template>
    <!-- MBR087 Contact Us — member_app_UI/song_fa_members_contact_us -->
    <div class="page-header align-items-start min-vh-100 mbr-contact">
        <span class="mask bg-white"></span>
        <div class="container p-0">
            <header class="mbr-contact__bar">
                <button type="button" class="mbr-contact__back" :aria-label="$t('common.back')" @click="goBack()">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" d="m15 18-6-6 6-6" />
                    </svg>
                </button>
                <h1 class="mbr-contact__title">{{ $t('contact.title') }}</h1>
            </header>

            <main v-if="isShowLayout" class="mbr-contact__body">
                <p class="mbr-contact__intro">{{ $t('contact.intro') }}</p>

                <!-- MBR088 form. Name / email / mobile stay read-only, prefilled from get-profile. -->
                <form class="mbr-contact__form" novalidate @submit.prevent="sendMailContactUs()">
                    <div class="mbr-field">
                        <label class="mbr-field__label" for="name">{{ $t('contact.name') }}</label>
                        <input id="name" v-model="form.name" type="text" class="mbr-field__input" readonly />
                    </div>

                    <div class="mbr-field">
                        <label class="mbr-field__label" for="email">{{ $t('contact.email') }}</label>
                        <input id="email" v-model="form.email" type="email" class="mbr-field__input" readonly />
                    </div>

                    <div class="mbr-field">
                        <label class="mbr-field__label" for="phone_number">{{ $t('contact.mobile') }} <span class="mbr-field__req">*</span></label>
                        <div class="mbr-field__input mbr-field__phone" :class="{ 'mbr-field__input--error': required.isPhoneRequire }">
                            <span class="mbr-field__prefix">+65</span>
                            <input id="phone_number" v-model="form.phone_number" type="tel" class="mbr-field__phone-input" readonly />
                        </div>
                        <span v-if="required.isPhoneRequire" class="mbr-field__error">{{ $t('contact.field_required') }}</span>
                    </div>

                    <div class="mbr-field">
                        <label class="mbr-field__label" for="topic">{{ $t('contact.topic') }} <span class="mbr-field__req">*</span></label>
                        <!-- MBR118 opens the MBR bottom sheet below instead of the native <select> list -->
                        <button
                            id="topic"
                            type="button"
                            class="mbr-field__input mbr-field__select"
                            :class="{ 'mbr-field__input--empty': !form.topic, 'mbr-field__input--error': required.isTopicOfEquiryRequire }"
                            aria-haspopup="listbox"
                            :aria-expanded="picker === 'topic' ? 'true' : 'false'"
                            @click="openPicker('topic', $event)"
                        >
                            <span class="mbr-field__select-text">{{ form.topic ? (topicLabels[form.topic] || form.topic) : $t('contact.select_topic') }}</span>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" d="m6 9 6 6 6-6" />
                            </svg>
                        </button>
                        <span v-if="required.isTopicOfEquiryRequire" class="mbr-field__error">{{ $t('contact.field_required') }}</span>
                    </div>

                    <!-- still sent as the branch name in `stores`, like the old free-text field -->
                    <div class="mbr-field">
                        <label class="mbr-field__label" for="stores">{{ $t('contact.store') }}</label>
                        <button
                            id="stores"
                            type="button"
                            class="mbr-field__input mbr-field__select"
                            :class="{ 'mbr-field__input--empty': !form.stores }"
                            aria-haspopup="listbox"
                            :aria-expanded="picker === 'stores' ? 'true' : 'false'"
                            @click="openPicker('stores', $event)"
                        >
                            <span class="mbr-field__select-text">{{ form.stores || $t('contact.select_store') }}</span>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" d="m6 9 6 6 6-6" />
                            </svg>
                        </button>
                    </div>

                    <div class="mbr-field">
                        <label class="mbr-field__label" for="order_number">{{ $t('contact.order_number') }}</label>
                        <input
                            id="order_number"
                            v-model="form.order_number"
                            type="text"
                            class="mbr-field__input mbr-field__input--caps"
                            :placeholder="$t('contact.order_placeholder', { example: 'SANGTEST-00012683' })"
                        />
                    </div>

                    <div class="mbr-field">
                        <label class="mbr-field__label" for="message">{{ $t('contact.message') }} <span class="mbr-field__req">*</span></label>
                        <textarea
                            id="message"
                            v-model="form.message"
                            class="mbr-field__input mbr-field__textarea"
                            :class="{ 'mbr-field__input--error': required.isMessageRequire }"
                            :placeholder="$t('contact.message_placeholder')"
                        ></textarea>
                        <span v-if="required.isMessageRequire" class="mbr-field__error">{{ $t('contact.field_required') }}</span>
                    </div>
                </form>
            </main>
        </div>

        <!-- MBR088 sticky Submit bar -->
        <div v-if="isShowLayout" class="mbr-contact__ctabar">
            <div class="mbr-contact__ctabar-inner">
                <button type="button" class="mbr-contact__cta" :disabled="disableBtnSubmit" @click="sendMailContactUs()">
                    <span>{{ $t('common.submit') }}</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14m-6-6 6 6-6 6" />
                    </svg>
                </button>
            </div>
        </div>

        <!-- MBR118 Topic / Store picker sheet (teleported to body) -->
        <teleport to="body">
            <transition name="mbr-contact-sheet">
                <div v-if="picker" class="mbr-contact-sheet" @click.self="closePicker()">
                    <div ref="pickerPanel" class="mbr-contact-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="mbr-contact-sheet-title">
                        <div class="mbr-contact-sheet__top">
                            <span class="mbr-contact-sheet__handle" aria-hidden="true"></span>
                            <button type="button" class="mbr-contact-sheet__close" :aria-label="$t('common.close')" @click="closePicker()">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <h2 id="mbr-contact-sheet-title" class="mbr-contact-sheet__title">
                            {{ picker === 'topic' ? $t('contact.topic') : $t('contact.store') }}
                        </h2>
                        <ul class="mbr-contact-sheet__list" role="listbox" aria-labelledby="mbr-contact-sheet-title">
                            <li v-for="(option, index) in pickerOptions" :key="index">
                                <button
                                    type="button"
                                    class="mbr-contact-sheet__option"
                                    :class="{ 'mbr-contact-sheet__option--active': option.value === form[picker] }"
                                    role="option"
                                    :aria-selected="option.value === form[picker] ? 'true' : 'false'"
                                    @click="choosePicker(option.value)"
                                >
                                    <span class="mbr-contact-sheet__label" :class="{ 'mbr-contact-sheet__label--muted': option.value === '' }">{{ option.label }}</span>
                                    <svg v-if="option.value === form[picker]" class="mbr-contact-sheet__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                    </svg>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </transition>
        </teleport>

        <!-- MBR088 success modal. The design's "Ref #" line is dropped: the API returns no reference. -->
        <teleport to="body">
            <transition name="mbr-contact-modal">
                <div v-if="showSuccess" class="mbr-contact-modal" @click.self="closeSuccess()">
                    <div class="mbr-contact-modal__panel" role="dialog" aria-modal="true" aria-labelledby="mbr-contact-sent-title">
                        <button type="button" class="mbr-contact-modal__close" :aria-label="$t('common.close')" @click="closeSuccess()">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M18 6 6 18M6 6l12 12" />
                            </svg>
                        </button>
                        <div class="mbr-contact-modal__icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8">
                                <polyline stroke-linecap="round" stroke-linejoin="round" points="20 6 9 17 4 12" />
                            </svg>
                        </div>
                        <h2 id="mbr-contact-sent-title" class="mbr-contact-modal__title">{{ $t('contact.sent_title') }}</h2>
                        <p class="mbr-contact-modal__text">{{ $t('contact.sent_text') }}</p>
                        <button type="button" class="mbr-contact__cta" @click="goHome()">{{ $t('contact.back_home') }}</button>
                    </div>
                </div>
            </transition>
        </teleport>

        <div class="position-fixed top-1 end-1 z-index-2">
            <material-snackbar v-if="snackbar === 'danger' || snackbar === 'success'" title="" date=""
                :description="message" :icon="{ component: 'campaign', color: 'white' }" :color="snackbar"
                :close-handler="closeSnackbar" />
        </div>
        <material-loading :active="loaderActive" />
    </div>
</template>

<script>
import { mapMutations } from "vuex";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MemberService from "../services/member.service";
import MerchantService from "../services/merchant.service";
import MaterialLoading from "@/components/MaterialLoading.vue";
import { getMerchantCode } from "@/config/utils";
import { errorMessage } from "@/utils/errorMessage";

export default {
    name: "contact-us",
    components: {
        MaterialSnackbar,
        MaterialLoading
    },
    data() {
        return {
            form: {
                name: '',
                email: '',
                phone_number: '',
                topic: '',
                stores: '',
                order_number: '',
                message: '',
            },
            topicOfEquiry: [
                'Stores',
                'Products',
                'Delivery Services',
                'Membership Portal'
            ],
            branches: [],
            loaderActive: false,
            snackbar: null,
            message: '',
            required: {
                isPhoneRequire: false,
                isTopicOfEquiryRequire: false,
                isMessageRequire: false,
            },
            merchantCode: "",
            isShowLayout: false,
            disableBtnSubmit: false,
            showSuccess: false,
            // MBR118 which picker sheet is open: 'topic' | 'stores' | null
            picker: null,
            pickerTrigger: null
        };
    },
    computed: {
        // topic values are still sent in English; only the option text is translated
        topicLabels() {
            return {
                Stores: this.$t("contact.topic_stores"),
                Products: this.$t("contact.topic_products"),
                "Delivery Services": this.$t("contact.topic_delivery"),
                "Membership Portal": this.$t("contact.topic_membership"),
            };
        },
        // Store keeps an empty row to clear the optional field, like the old placeholder option
        pickerOptions() {
            if (this.picker === 'topic') {
                return this.topicOfEquiry.map((value) => ({ value, label: this.topicLabels[value] || value }));
            }
            if (this.picker === 'stores') {
                return [{ value: '', label: this.$t('contact.select_store') }].concat(
                    this.branches.map((branch) => ({ value: branch.branch_name, label: branch.branch_name }))
                );
            }
            return [];
        },
    },
    created() {
        this.getMemberInfo()
        this.getBranches()
    },
    // draws its own header like the design: hide the top navbar, and keep the
    // sidenav as an off-screen drawer (same as Find Branches)
    beforeMount() {
        this.toggleNavbarOnly();
        this.toggleHideConfig();
        const app = document.querySelector("#app");
        app.classList.add("mbr-sidenav-overlay");
        app.classList.remove("g-sidenav-pinned");
    },
    mounted() {
        const code = getMerchantCode();
        this.merchantCode = code ? `/${code}` : ''
    },
    beforeUnmount() {
        document.removeEventListener("keydown", this.onPickerKeydown);
        this.toggleNavbarOnly();
        this.toggleHideConfig();
        const app = document.querySelector("#app");
        app.classList.remove("mbr-sidenav-overlay");
        if (window.innerWidth > 1200) {
            app.classList.add("g-sidenav-pinned");
        }
    },
    methods: {
        ...mapMutations(["toggleNavbarOnly", "toggleHideConfig"]),
        goBack() {
            this.$router.push(this.merchantCode + "/account");
        },
        goHome() {
            this.showSuccess = false
            this.$router.push(this.merchantCode + "/home");
        },
        closeSuccess() {
            this.showSuccess = false
        },
        openPicker(field, event) {
            this.pickerTrigger = event.currentTarget
            this.picker = field
            document.addEventListener("keydown", this.onPickerKeydown)
            // move focus into the sheet, onto the selected row when there is one
            this.$nextTick(() => {
                const panel = this.$refs.pickerPanel
                if (!panel) return
                const target = panel.querySelector('[aria-selected="true"]') || panel.querySelector('[role="option"]')
                if (target) target.focus({ preventScroll: false })
            })
        },
        closePicker() {
            this.picker = null
            document.removeEventListener("keydown", this.onPickerKeydown)
            if (this.pickerTrigger) this.pickerTrigger.focus()
            this.pickerTrigger = null
        },
        choosePicker(value) {
            this.form[this.picker] = value
            this.closePicker()
        },
        onPickerKeydown(event) {
            if (event.key === "Escape") this.closePicker()
        },
        closeSnackbar() {
            this.snackbar = null;
        },
        getMemberInfo() {
            this.showLoader()
            MemberService.getProfile().then(
                (response) => {
                    if (response.data.result.isSuccess) {
                        this.memberInfo = response.data.data
                        this.form.name = this.memberInfo.name ? this.memberInfo.name : ''
                        this.form.email = this.memberInfo.email ? this.memberInfo.email : ''
                        this.form.phone_number = this.memberInfo.phone_number ? this.memberInfo.phone_number : ''
                        this.isShowLayout = true
                    } else {
                        this.snackbar = 'danger'
                        this.message = errorMessage(response.data.result.message)
                        this.setTimeOutNotification()
                    }
                    this.hideLoader()
                },
                (error) => {
                    this.snackbar = 'danger'
                    this.message = errorMessage(error)
                    this.setTimeOutNotification()
                    this.hideLoader()
                }
            );
        },
        // the Store field is optional, so a failed branch list just leaves the dropdown empty
        getBranches() {
            MerchantService.getBranches('').then(
                (response) => {
                    if (response.data.result.isSuccess) {
                        this.branches = response.data.data.branches || []
                    }
                },
                () => {}
            );
        },
        sendMailContactUs() {
            if (this.disableBtnSubmit) return
            this.disableBtnSubmit = true
            this.closeSnackbar()
            if (this.processValidate()) {
                this.showLoader()
                MemberService.sendMailContact(this.form).then(
                    (response) => {
                        if (response.data.result.isSuccess) {
                            this.form.topic = ''
                            this.form.stores = ''
                            this.form.order_number = ''
                            this.form.message = ''
                            this.showSuccess = true
                        } else {
                            this.snackbar = 'danger'
                            this.message = errorMessage(response.data.result.message)
                            this.setTimeOutNotification()
                        }
                        this.hideLoader()
                        this.disableBtnSubmit = false
                    },
                    (error) => {
                        this.snackbar = 'danger'
                        this.message = errorMessage(error)
                        this.setTimeOutNotification()
                        this.hideLoader()
                        this.disableBtnSubmit = false
                    }
                );
            } else {
                this.disableBtnSubmit = false
            }
        },
        setTimeOutNotification() {
            setTimeout(() => { this.snackbar = null; }, 3000);
        },
        resetRequired() {
            this.required.isPhoneRequire = false;
            this.required.isTopicOfEquiryRequire = false;
            this.required.isMessageRequire = false;
        },
        processValidate() {
            this.resetRequired();
            var isValid = true;
            if (this.form.phone_number == '') {
                this.required.isPhoneRequire = true;
                isValid = false;
            }
            if (this.form.topic == '') {
                this.required.isTopicOfEquiryRequire = true;
                isValid = false;
            }
            if (this.form.message == '') {
                this.required.isMessageRequire = true;
                isValid = false;
            }
            return isValid;
        },
        showLoader() {
            this.loaderActive = true;
        },

        hideLoader() {
            this.loaderActive = false;
        },
    },
};
</script>

<style scoped>
/* MBR087 Contact Us. The old unscoped block (.pl-0, .monthly-group, .btn-share-profile,
   .title-share-profile) is dropped: no Contact Us markup uses those classes. */
.mbr-contact.page-header {
    overflow: visible;
    overflow-x: clip;
    font-family: var(--mbr-font-body);
    color: var(--mbr-color-ink-headline);
}

/* custom.css still carries the legacy `* { font-family: Poppins }`; icon fonts (the
   snackbar's Font Awesome glyphs) are left alone */
.mbr-contact :where(:not(.material-icons-round):not(.fas)) {
    font-family: var(--mbr-font-body);
}

.mbr-contact .mask {
    background: var(--mbr-color-canvas) !important;
    opacity: 1;
}

.mbr-contact .container {
    max-width: 440px;
    margin: 0 auto;
}

/* header */
.mbr-contact__bar {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    padding: 24px 20px 12px;
    background: var(--mbr-nav-bg);
    backdrop-filter: blur(var(--mbr-nav-blur));
    -webkit-backdrop-filter: blur(var(--mbr-nav-blur));
}

.mbr-contact__back {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    background: var(--mbr-color-surface);
    border: 1px solid var(--mbr-color-outline);
    border-radius: var(--mbr-radius-full);
    box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
    color: var(--mbr-color-primary);
    transition: transform 0.15s ease, background-color 0.15s ease;
}

.mbr-contact__back:hover {
    background: var(--mbr-color-tint);
}

.mbr-contact__back:active {
    transform: scale(0.95);
}

.mbr-contact__back svg {
    width: 18px;
    height: 18px;
}

/* centred title, balanced against the 40px back button */
.mbr-contact__title {
    flex: 1;
    margin: 0;
    padding-right: 40px;
    text-align: center;
    font-family: var(--mbr-font-headline) !important;
    font-size: 22px;
    font-weight: 800;
    line-height: 28px;
    letter-spacing: -0.02em;
    color: var(--mbr-color-ink-strong);
}

.mbr-contact__body {
    padding: 4px 20px calc(120px + env(safe-area-inset-bottom));
}

.mbr-contact__intro {
    margin: 0 0 24px;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.6;
    color: var(--mbr-color-ink-secondary);
}

/* MBR088 form fields */
.mbr-contact__form {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.mbr-field__label {
    display: block;
    margin: 0 0 6px;
    font-size: var(--mbr-label-caps-size);
    font-weight: var(--mbr-label-caps-weight);
    line-height: var(--mbr-label-caps-line);
    letter-spacing: var(--mbr-label-caps-tracking);
    text-transform: uppercase;
    color: var(--mbr-color-ink-secondary);
}

.mbr-field__req {
    color: var(--mbr-color-primary);
}

.mbr-field__input {
    display: block;
    width: 100%;
    height: 56px;
    margin: 0;
    padding: 0 16px;
    background: var(--mbr-color-surface);
    border: 1.5px solid var(--mbr-color-outline);
    border-radius: var(--mbr-radius-lg);
    font-size: 15px;
    font-weight: 500;
    color: var(--mbr-color-ink-strong);
    outline: none;
    box-shadow: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.mbr-field__input:focus,
.mbr-field__input:focus-within {
    border-color: var(--mbr-color-primary);
    box-shadow: 0 0 0 1px var(--mbr-color-primary);
}

/* read-only fields don't take focus styling: the member can't edit them here */
.mbr-field__input[readonly]:focus {
    border-color: var(--mbr-color-outline);
    box-shadow: none;
}

.mbr-field__input::placeholder {
    text-align: start;
    font-size: 15px;
    color: rgba(var(--mbr-color-ink-secondary-rgb), 0.45);
}

.mbr-field__input--caps,
.mbr-field__input--caps::placeholder {
    text-transform: uppercase;
}

.mbr-field__input--error {
    border-color: var(--mbr-color-error);
}

.mbr-field__phone {
    display: flex;
    align-items: center;
    padding: 0 12px;
}

.mbr-field__phone:focus-within {
    border-color: var(--mbr-color-outline);
    box-shadow: none;
}

.mbr-field__prefix {
    flex: none;
    padding-right: 10px;
    border-right: 1px solid var(--mbr-color-outline);
    font-size: 15px;
    font-weight: 600;
    color: var(--mbr-color-ink-strong);
}

.mbr-field__phone-input {
    flex: 1;
    min-width: 0;
    height: 100%;
    padding: 0 0 0 12px;
    background: transparent;
    border: 0;
    outline: none;
    font-size: 15px;
    font-weight: 500;
    color: var(--mbr-color-ink-strong);
}

.mbr-field__select {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    text-align: left;
    cursor: pointer;
}

.mbr-field__select-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.mbr-field__select.mbr-field__input--empty {
    color: rgba(var(--mbr-color-ink-secondary-rgb), 0.45);
}

.mbr-field__select svg {
    flex: none;
    width: 16px;
    height: 16px;
    color: var(--mbr-color-primary);
}

.mbr-field__textarea {
    height: 120px;
    padding: 14px;
    line-height: 1.5;
    resize: none;
}

.mbr-field__error {
    display: block;
    margin-top: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--mbr-color-error);
}

/* MBR088 sticky Submit bar */
.mbr-contact__ctabar {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1030;
    background: rgba(var(--mbr-color-canvas-rgb), 0.9);
    border-top: 1px solid rgba(var(--mbr-color-outline-rgb), 0.6);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
}

.mbr-contact__ctabar-inner {
    max-width: 440px;
    margin: 0 auto;
    padding: 20px 20px calc(20px + env(safe-area-inset-bottom));
}

.mbr-contact__cta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 56px;
    border: 0;
    border-radius: var(--mbr-radius-full);
    background: var(--mbr-color-primary);
    box-shadow: 0 10px 15px -3px rgba(var(--mbr-color-primary-rgb), 0.2);
    font-family: var(--mbr-font-headline) !important;
    font-size: 16px;
    font-weight: 700;
    color: var(--mbr-color-on-primary);
    transition: transform 0.15s ease, background-color 0.15s ease;
}

.mbr-contact__cta span {
    font-family: var(--mbr-font-headline) !important;
}

.mbr-contact__cta:hover:not(:disabled) {
    background: var(--mbr-color-primary-dark);
}

.mbr-contact__cta:active:not(:disabled) {
    transform: scale(0.99);
}

.mbr-contact__cta:disabled {
    opacity: 0.4;
    box-shadow: none;
}

.mbr-contact__cta svg {
    width: 18px;
    height: 18px;
}

/* MBR118 Topic / Store picker sheet (teleported to body), same look as MbrLanguageSheet */
.mbr-contact-sheet {
    position: fixed;
    inset: 0;
    z-index: 1060;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: rgba(var(--mbr-color-ink-strong-rgb), 0.5);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
}

.mbr-contact-sheet__panel {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 440px;
    max-height: 80vh;
    padding: 12px 24px calc(32px + env(safe-area-inset-bottom));
    background: var(--mbr-color-surface);
    border-top: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
    border-radius: 32px 32px 0 0;
    box-shadow: var(--mbr-shadow-modal);
}

/* outside .mbr-contact here, so beat custom.css's `* { font-family: Poppins }` again */
.mbr-contact-sheet__panel * {
    font-family: var(--mbr-font-body);
}

.mbr-contact-sheet__top {
    position: relative;
    display: flex;
    flex: none;
    justify-content: flex-end;
    min-height: 36px;
}

.mbr-contact-sheet__handle {
    position: absolute;
    top: 0;
    left: 50%;
    width: 40px;
    height: 4px;
    background: var(--mbr-color-outline);
    border-radius: var(--mbr-radius-full);
    transform: translateX(-50%);
}

.mbr-contact-sheet__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    background: var(--mbr-color-tint);
    border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
    border-radius: var(--mbr-radius-full);
    color: var(--mbr-color-ink-secondary);
}

.mbr-contact-sheet__close svg {
    width: 16px;
    height: 16px;
}

/* Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-contact-sheet__title {
    flex: none;
    margin: 4px 0 16px;
    font-family: var(--mbr-font-headline) !important;
    font-size: var(--mbr-headline-sm-size);
    font-weight: var(--mbr-headline-sm-weight);
    line-height: var(--mbr-headline-sm-line);
    letter-spacing: var(--mbr-headline-sm-tracking);
    color: var(--mbr-color-ink-headline);
}

/* long branch lists scroll inside the sheet */
.mbr-contact-sheet__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 0;
    margin: 0 -4px;
    padding: 4px;
    overflow-y: auto;
    list-style: none;
    overscroll-behavior: contain;
}

.mbr-contact-sheet__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    min-height: 56px;
    padding: 10px 16px;
    background: var(--mbr-color-tint);
    border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
    border-radius: var(--mbr-radius-lg);
    color: var(--mbr-color-ink-headline);
    text-align: left;
    transition: transform 0.12s ease;
}

.mbr-contact-sheet__option:active {
    transform: scale(0.98);
}

.mbr-contact-sheet__option:focus-visible,
.mbr-contact-sheet__close:focus-visible {
    outline: 2px solid var(--mbr-color-primary);
    outline-offset: 2px;
}

.mbr-contact-sheet__option--active {
    background: var(--mbr-color-surface);
    border-color: var(--mbr-color-primary);
}

.mbr-contact-sheet__label {
    min-width: 0;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
}

.mbr-contact-sheet__label--muted {
    font-weight: 500;
    color: var(--mbr-color-ink-muted);
}

.mbr-contact-sheet__check {
    flex: none;
    width: 20px;
    height: 20px;
    color: var(--mbr-color-primary);
}

.mbr-contact-sheet-enter-active,
.mbr-contact-sheet-leave-active {
    transition: opacity 0.2s ease;
}

.mbr-contact-sheet-enter-active .mbr-contact-sheet__panel,
.mbr-contact-sheet-leave-active .mbr-contact-sheet__panel {
    transition: transform 0.25s ease;
}

.mbr-contact-sheet-enter-from,
.mbr-contact-sheet-leave-to {
    opacity: 0;
}

.mbr-contact-sheet-enter-from .mbr-contact-sheet__panel,
.mbr-contact-sheet-leave-to .mbr-contact-sheet__panel {
    transform: translateY(100%);
}

/* MBR088 success modal (teleported to body) */
.mbr-contact-modal {
    position: fixed;
    inset: 0;
    z-index: 1060;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding: 16px 16px calc(32px + env(safe-area-inset-bottom));
    background: rgba(var(--mbr-color-ink-strong-rgb), 0.5);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    font-family: var(--mbr-font-body);
}

.mbr-contact-modal__panel {
    position: relative;
    width: 100%;
    max-width: 408px;
    padding: 24px;
    background: var(--mbr-color-surface);
    border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
    border-radius: 32px;
    box-shadow: 0 25px 50px -12px rgba(var(--mbr-color-shadow-rgb), 0.25);
    text-align: center;
}

.mbr-contact-modal__close {
    position: absolute;
    top: 16px;
    right: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: var(--mbr-color-tint);
    border: 0;
    border-radius: var(--mbr-radius-full);
    color: var(--mbr-color-ink-secondary);
    transition: color 0.15s ease;
}

.mbr-contact-modal__close:hover {
    color: var(--mbr-color-ink-strong);
}

.mbr-contact-modal__close svg {
    width: 14px;
    height: 14px;
}

.mbr-contact-modal__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    margin: 8px auto 16px;
    background: var(--mbr-color-peach);
    border-radius: var(--mbr-radius-full);
    box-shadow: 0 1px 2px rgba(var(--mbr-color-shadow-rgb), 0.05);
    color: var(--mbr-color-primary);
}

.mbr-contact-modal__icon svg {
    width: 30px;
    height: 30px;
}

.mbr-contact-modal__title {
    margin: 0 0 8px;
    font-family: var(--mbr-font-headline) !important;
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: var(--mbr-color-ink-strong);
}

.mbr-contact-modal__text {
    margin: 0 0 24px;
    padding: 0 8px;
    font-family: var(--mbr-font-body);
    font-size: 14px;
    line-height: 1.6;
    color: var(--mbr-color-ink-secondary);
}

.mbr-contact-modal-enter-active,
.mbr-contact-modal-leave-active {
    transition: opacity 0.2s ease;
}

.mbr-contact-modal-enter-active .mbr-contact-modal__panel,
.mbr-contact-modal-leave-active .mbr-contact-modal__panel {
    transition: transform 0.2s ease;
}

.mbr-contact-modal-enter-from,
.mbr-contact-modal-leave-to {
    opacity: 0;
}

.mbr-contact-modal-enter-from .mbr-contact-modal__panel,
.mbr-contact-modal-leave-to .mbr-contact-modal__panel {
    transform: translateY(24px);
}
</style>
